import React, { useEffect, useState } from 'react';
import {
  addServiceTicketTimelineEntry,
  getServiceTicketDetails,
  listServiceDeskTickets,
  updateServiceTicketStatus,
} from '../../api/backend';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'en_espera_cliente', label: 'En espera del cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' },
];

const PRIORITY_LABELS = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  critica: 'Crítica',
};

const PRIORITY_STYLES = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = value.toDate?.() || new Date(value);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-PR', { month: 'short', day: 'numeric' });
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString('es-PR', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : '—';
};

const ServiceDesk = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('abierto');
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchTickets = async () => {
      try {
        const list = await listServiceDeskTickets();
        if (!cancelled) {
          setTickets(list);
        }
      } catch (err) {
        console.error('❌ Error cargando tickets globales:', err);
      }
    };

    fetchTickets();
    const intervalId = window.setInterval(fetchTickets, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter((t) => (t.status || 'abierto') === statusFilter));
    }
  }, [tickets, statusFilter]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingTimeline(true);
    try {
      const response = await getServiceTicketDetails({ clientId: ticket.clientId, ticketId: ticket.id });
      setSelectedTicket({ ...response.ticket, clientId: ticket.clientId });
      setTimeline(response.timeline || []);
    } catch (err) {
      console.error('❌ Error cargando timeline del ticket:', err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const addTimelineEntry = async () => {
    if (!selectedTicket || !note.trim()) return;
    try {
      const response = await addServiceTicketTimelineEntry({
        clientId: selectedTicket.clientId,
        ticketId: selectedTicket.id,
        summary: note.trim(),
      });
      setNote('');
      setTimeline(response.timeline || []);
      const refreshed = await getServiceTicketDetails({
        clientId: selectedTicket.clientId,
        ticketId: selectedTicket.id,
      });
      setSelectedTicket(refreshed.ticket);
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.id === refreshed.ticket.id && ticket.clientId === selectedTicket.clientId
            ? { ...refreshed.ticket, clientId: selectedTicket.clientId }
            : ticket
        )
      );
    } catch (err) {
      console.error('❌ Error añadiendo nota de servicio:', err);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      const updatedTicket = await updateServiceTicketStatus({
        clientId: selectedTicket.clientId,
        ticketId: selectedTicket.id,
        status,
      });
      setTickets((prev) =>
        prev.map((t) =>
          t.id === updatedTicket.id && t.clientId === selectedTicket.clientId
            ? { ...updatedTicket, clientId: selectedTicket.clientId }
            : t
        )
      );
      setSelectedTicket((prev) => prev && { ...updatedTicket, clientId: prev.clientId });
    } catch (err) {
      console.error('❌ Error actualizando estado del ticket:', err);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Centro de servicios</h1>
          <p className="text-sm text-gray-500">Gestiona las solicitudes que han abierto los clientes.</p>
        </div>
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lista de tickets */}
        <section className="bg-white rounded-xl shadow divide-y divide-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 bg-gray-50">
            <h2 className="text-base font-semibold text-gray-800">Tickets</h2>
            <span className="text-xs font-medium bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
              {filteredTickets.length} resultado{filteredTickets.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            {filteredTickets.map((ticket) => {
              const isSelected = selectedTicket && ticket.id === selectedTicket.id && ticket.clientId === selectedTicket.clientId;
              return (
                <article
                  key={`${ticket.clientId}-${ticket.id}`}
                  className={`px-5 py-4 cursor-pointer transition-colors ${
                    isSelected ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                  onClick={() => openTicket(ticket)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 text-sm leading-snug">{ticket.title}</h3>
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[ticket.priority] || 'bg-gray-100 text-gray-600'}`}>
                      {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{ticket.clientName || ticket.clientId}</p>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
                    <span className="capitalize">{STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}</span>
                    <span>{formatDate(ticket.updatedAt || ticket.createdAt)}</span>
                  </div>
                </article>
              );
            })}
            {filteredTickets.length === 0 && (
              <p className="px-5 py-10 text-sm text-center text-gray-400">No hay tickets con este filtro.</p>
            )}
          </div>
        </section>

        {/* Detalle del ticket */}
        <section className="bg-white rounded-xl shadow overflow-hidden flex flex-col">
          {selectedTicket ? (
            <>
              {/* Cabecera del ticket */}
              <div className="px-6 py-5 bg-gray-50 border-b border-gray-100 flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Ticket</p>
                  <h2 className="text-xl font-semibold text-gray-900 leading-snug">{selectedTicket.title}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{selectedTicket.clientName || selectedTicket.clientEmail}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[selectedTicket.priority] || 'bg-gray-100'}`}>
                      {PRIORITY_LABELS[selectedTicket.priority] || selectedTicket.priority || 'Sin prioridad'}
                    </span>
                  </div>
                </div>
                <select
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  value={selectedTicket.status}
                  onChange={(e) => updateStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Timeline */}
              <div className="flex-1 px-6 py-4 overflow-y-auto max-h-72">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Historial de actualizaciones</h3>
                {loadingTimeline ? (
                  <p className="text-sm text-gray-400">Cargando…</p>
                ) : timeline.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">Sin actualizaciones aún.</p>
                ) : (
                  <ul className="space-y-3">
                    {timeline.map((entry) => {
                      const isAdmin = entry.channel === 'admin' || (entry.createdBy && !entry.createdBy.includes('cliente'));
                      return (
                        <li
                          key={entry.id}
                          className={`rounded-xl px-4 py-3 text-sm border ${
                            isAdmin
                              ? 'bg-blue-50 border-blue-100'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className={`text-xs font-semibold ${isAdmin ? 'text-blue-700' : 'text-gray-600'}`}>
                              {isAdmin ? 'Equipo BBTS' : 'Cliente'}
                            </span>
                            <span className="text-xs text-gray-400">{formatDateTime(entry.createdAt)}</span>
                          </div>
                          <p className="text-gray-800 leading-snug">{entry.summary}</p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Input de nota */}
              <div className="px-6 py-4 border-t border-gray-100 bg-white space-y-3">
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 placeholder-gray-400"
                  placeholder="Escribe una actualización para el cliente…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  type="button"
                  onClick={addTimelineEntry}
                  disabled={!note.trim()}
                  className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                >
                  Enviar actualización
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-24 px-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl">📋</div>
              <p className="text-sm text-center">Selecciona un ticket de la lista para ver sus detalles y gestionar la comunicación.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ServiceDesk;
